import React, { Component } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Container, Divider, Grid, Header, Button } from "semantic-ui-react";
import { internalLocationApi } from "@api/locations";
import { itemApi } from "@api/items"; // Added import
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { ResultsTable } from "@components/ResultsTable/ResultsTable";
import { DeleteRecordModal } from "@components/backoffice/DeleteRecordModal";
import { NewButton } from "@components/backoffice/buttons/NewButton";
import { BackOfficeRoutes } from "@routes/urls";
import { goTo } from "@history";
import _get from "lodash/get";
import { InternalLocationList } from "./InternalLocationList";
import { AuditService } from "@api/audit/audit";
import { userApi } from "@api/users/user";

export default class LocationList extends Component {
  constructor(props) {
    super(props);
    this.fetchAllLocations = props.fetchAllLocations;
    this.deleteLocation = props.deleteLocation;
  }

  componentDidMount() {
    this.fetchAllLocations();
  }

  createRefProps(locationPid) {
    return [
      {
        refType: "Internal Location",
        onRefClick: (iLocPid) => {
          goTo(BackOfficeRoutes.ilocationsEditFor(iLocPid));
        },
        getRefData: () =>
          internalLocationApi.list(`location_pid:${locationPid}`),
      },
      {
        refType: "Physical Copies (Items)",
        onRefClick: (itemPid) => {
          goTo(BackOfficeRoutes.itemDetailsFor(itemPid));
        },
        getRefData: async () => {
          try {
            // First get all internal locations for this location
            const internalLocationsResponse = await internalLocationApi.list(
              `location_pid:${locationPid}`
            );
            const internalLocationPids =
              internalLocationsResponse.data.hits.map(
                (iloc) => iloc.metadata.pid
              );

            if (internalLocationPids.length === 0) {
              return { data: { hits: [], total: 0 } };
            }

            // Then find all items that reference any of these internal locations
            const query = `(${internalLocationPids
              .map((pid) => `internal_location_pid:"${pid}"`)
              .join(" OR ")})`;
            const itemsResponse = await itemApi.list(query);
            return itemsResponse;
          } catch (error) {
            return { data: { hits: [], total: 0 } };
          }
        },
      },
    ];
  }

  handleDeleteLocation = async (locationPid, locationName) => {
    await this.deleteLocation(locationPid);

    const userResponse = await userApi.get();
    const currentUser = userResponse.data;
    const action = "Delete Location";
    const details = `The user ${currentUser.full_name} (${currentUser.username}) deleted the location "${locationName}" (PID: ${locationPid}).`;

    await AuditService.api.createLog({
      action,
      details,
      created_by: `${currentUser.full_name} (${currentUser.username})`,
    });
  };

  rowActions = ({ row }) => {
    return (
      <>
        <Button
          as={Link}
          to={BackOfficeRoutes.locationsEditFor(row.metadata.pid)}
          icon="edit"
          size="small"
          title="Edit Record"
        />
        <DeleteRecordModal
          deleteHeader={`Are you sure you want to delete the Location record with ID ${row.metadata.pid}?`}
          onDelete={() =>
            this.handleDeleteLocation(row.metadata.pid, row.metadata.name)
          }
          refProps={this.createRefProps(row.metadata.pid)}
        />
      </>
    );
  };

  renderResults(data) {
    const headerActionComponent = (
      <NewButton text="New location" to={BackOfficeRoutes.locationsCreate} />
    );

    const viewDetails = ({ row, col }) => {
      return (
        <Link to={BackOfficeRoutes.locationsDetailsFor(row.metadata.pid)}>
          {_get(row, col.field)}
        </Link>
      );
    };

    const columns = [
      { title: "ID", field: "metadata.pid", formatter: viewDetails },
      { title: "Name", field: "metadata.name" },
      { title: "Address", field: "metadata.address" },
      { title: "Email", field: "metadata.email" },
      { title: "Actions", field: "", formatter: this.rowActions },
    ];

    return (
      <ResultsTable
        data={data.hits}
        columns={columns}
        totalHitsCount={data.total}
        title="Locations"
        name="locations"
        headerActionComponent={headerActionComponent}
      />
    );
  }

  render() {
    let { data, isLoading, error } = this.props;

    return (
      <Container fluid>
        <Header as="h2">Physical locations</Header>
        <Grid>
          <Grid.Column width={16}>
            <Container fluid className="spaced">
              <Loader isLoading={isLoading}>
                <Error error={error}>{this.renderResults(data)}</Error>
              </Loader>
            </Container>
            <br />
            <Divider />
            <br />
            <Container fluid className="spaced">
              <InternalLocationList />
            </Container>
          </Grid.Column>
        </Grid>
      </Container>
    );
  }
}

LocationList.propTypes = {
  data: PropTypes.object.isRequired,
  fetchAllLocations: PropTypes.func.isRequired,
  deleteLocation: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.object,
};

LocationList.defaultProps = {
  error: null,
};
